import type { SupabaseClient } from '../../lib/supabase';

/**
 * Quick emoji reactions on a comment (Messaging Drop 3b-2), stored in
 * public.comment_reactions (20270181000100). One row per person per emoji per
 * comment. Everyone who can view the comment's project reads them; only an
 * active TAP admin or member adds or removes their own. The database decides
 * all of that (RLS); nothing here is trusted for permission.
 *
 * The insert names only comment_id and emoji: user_id defaults to the caller
 * and created_at to now(). The database refuses a browser that sends
 * created_at (column grant, fix-drop3b2-db-dev4.patch).
 *
 * The emoji column accepts any short text without spaces or angle brackets,
 * so a stored value is only ever rendered as React text, never as HTML and
 * never inside a string-built attribute (findings-dev4-drop3b2-db.md item 7).
 */

export interface ReactionSummary {
  emoji: string;
  count: number;
  /** The signed-in person reacted with this emoji. */
  mine: boolean;
}

interface ReactionRow {
  comment_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

/** The same rule as comment_reactions_emoji_length and _emoji_shape. */
export function isReactionEmoji(value: string): boolean {
  const length = [...value].length;
  if (length < 1 || length > 16) {
    return false;
  }
  return !/[\s<>]/u.test(value);
}

/**
 * Rows grouped per comment, each emoji once with its count, in the order the
 * first person used it (created_at), so chips do not jump around.
 */
export function summarizeReactions(
  rows: ReactionRow[],
  myId: string,
): Map<string, ReactionSummary[]> {
  const ordered = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at));
  const out = new Map<string, ReactionSummary[]>();
  for (const row of ordered) {
    const list = out.get(row.comment_id) ?? [];
    const existing = list.find((entry) => entry.emoji === row.emoji);
    if (existing === undefined) {
      list.push({ emoji: row.emoji, count: 1, mine: row.user_id === myId });
    } else {
      existing.count += 1;
      existing.mine = existing.mine || row.user_id === myId;
    }
    out.set(row.comment_id, list);
  }
  return out;
}

export async function fetchReactions(
  client: SupabaseClient,
  commentIds: string[],
  myId: string,
): Promise<Map<string, ReactionSummary[]>> {
  if (commentIds.length === 0) {
    return new Map();
  }
  // A long thread puts every comment id in the URL. Ask in chunks, like
  // AGENT_IN_CHUNK in agentApi.ts, so a busy room cannot outgrow the URL limit
  // and lose every reaction at once (Dev4, t_8f7714ba).
  const rows: Parameters<typeof summarizeReactions>[0] = [];
  for (let at = 0; at < commentIds.length; at += REACTION_IN_CHUNK) {
    const { data, error } = await client
      .from('comment_reactions')
      .select('comment_id, user_id, emoji, created_at')
      .in('comment_id', commentIds.slice(at, at + REACTION_IN_CHUNK));
    if (error) {
      throw new Error(error.message);
    }
    rows.push(...(Array.isArray(data) ? data : []));
  }
  return summarizeReactions(rows, myId);
}

/** How many comment ids go into one `in (...)` filter. */
export const REACTION_IN_CHUNK = 200;

export async function addReaction(
  client: SupabaseClient,
  commentId: string,
  emoji: string,
): Promise<void> {
  if (!isReactionEmoji(emoji)) {
    throw new Error('reaction_not_an_emoji');
  }
  const { error } = await client.from('comment_reactions').insert({ comment_id: commentId, emoji });
  if (error) {
    throw new Error(error.message);
  }
}

export async function removeReaction(
  client: SupabaseClient,
  commentId: string,
  emoji: string,
  myId: string,
): Promise<void> {
  const { error } = await client
    .from('comment_reactions')
    .delete()
    .eq('comment_id', commentId)
    .eq('user_id', myId)
    .eq('emoji', emoji);
  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Who may add or remove a reaction: the same people the write policies admit
 * (comment_reactions_insert_own / _delete_own, 20270181000100): an active TAP
 * admin or member. Viewers and suppliers only read. The database still
 * checks the project on every write.
 */
export function mayReact(
  profile: { account_type: string; internal_role: string | null; is_active: boolean } | null,
): boolean {
  if (profile === null) {
    return false;
  }
  return (
    profile.is_active &&
    profile.account_type === 'tap' &&
    (profile.internal_role === 'tap_admin' || profile.internal_role === 'tap_member')
  );
}

/** Plain words for a refused reaction. */
export function reactionErrorMessage(raw: string): string {
  if (raw.includes('duplicate key')) {
    // Two clicks raced; the reaction is there, which is what was asked.
    return '';
  }
  if (raw.includes('row-level security') || raw.includes('permission denied')) {
    return 'You cannot react here.';
  }
  return 'That reaction did not save. Try again.';
}
